/**
 * Presentation helpers for the Queue tab's row grammar.
 *
 * Colour discipline (from the redesign research): red is reserved for
 * the due badge past breach + genuinely failed work; amber for
 * inside-the-warning-window; everything else stays quiet. Staleness on
 * waiting rows is a GREY note, never another red.
 */

import { InboxItem } from '@/types/inbox';
import { formatMoney } from '@/utils';

// ---------------------------------------------------------------------------
// Kind chips
// ---------------------------------------------------------------------------

export type ChipTone = 'gray' | 'blue' | 'teal' | 'violet' | 'orange' | 'red';

const KIND_CHIPS: Record<string, { label: string; tone: ChipTone }> = {
  enquiry_needs_triage: { label: 'Enquiry — awaiting triage', tone: 'gray' },
  triage_needed: { label: 'Request — in triage', tone: 'blue' },
  ready_to_start: { label: 'Ready to start', tone: 'blue' },
  report_pending_approval: { label: 'Report — pending approval', tone: 'teal' },
  report_pending_activation: { label: 'Awaiting client activation', tone: 'teal' },
  np_fulfilment_pending: { label: 'With partner', tone: 'blue' },
  report_failed: { label: 'Report failed', tone: 'red' },
  report_callback_dropped: { label: 'Report stranded', tone: 'red' },
  subject_disambiguation_needed: { label: 'Subject needs disambiguation', tone: 'red' },
  file_selection_required: { label: 'File selection required', tone: 'orange' },
  match_review_required: { label: 'Match review required', tone: 'orange' },
  profile_review_required: { label: 'Profiles need confirming', tone: 'orange' },
  np_resubmission: { label: 'Partner resubmitted', tone: 'orange' },
  np_assignment_needed: { label: 'Partners needed', tone: 'orange' },
  report_edit_no_changes: { label: 'Feedback made no changes', tone: 'orange' },
  // Registry documents ordered from Armadillo: one row per report. The
  // delivered row is routine review work; the overdue one means the
  // research desk needs chasing; the reply one means the desk wrote
  // without sending a document (a quote, a question) and the order does
  // not move until someone answers.
  registry_documents_delivered: { label: 'Registry documents received', tone: 'teal' },
  registry_document_overdue: { label: 'Registry documents overdue', tone: 'red' },
  registry_order_reply: { label: 'Registry order message', tone: 'orange' },
  // One row per conversation: "a client / partner wrote and nobody has
  // replied". A client message about a report that is already running
  // carries `extra.in_progress_report_ids` (the title says so too).
  chat_message_from_client: { label: 'Client awaiting reply', tone: 'violet' },
  chat_message_from_np: { label: 'Partner awaiting reply', tone: 'violet' },
  follow_up: { label: 'Follow-up', tone: 'gray' },
};

export function kindChip(kind: string): { label: string; tone: ChipTone } {
  return KIND_CHIPS[kind] ?? { label: kind.replace(/_/g, ' '), tone: 'gray' };
}

// ---------------------------------------------------------------------------
// The due badge (Zendesk-style: quiet / amber / red, coarse units)
// ---------------------------------------------------------------------------

export interface DueInfo {
  /** Coarse label, e.g. "3d", "26h", "1d over". */
  label: string;
  tone: 'quiet' | 'warning' | 'overdue';
  /** Exact deadline for the tooltip. */
  exact: string;
}

const HOUR = 3_600_000;
const DAY = 24 * HOUR;

function coarse(ms: number): string {
  if (ms >= 2 * DAY) {
    return `${Math.round(ms / DAY)}d`;
  }
  if (ms >= HOUR) {
    return `${Math.round(ms / HOUR)}h`;
  }
  return '<1h';
}

/**
 * The row's clock. Amber when inside the warning window — under 24h
 * remaining, or under 20% of the TAT when one is known. Red past due.
 * Null when the item has no deadline (unstarted work ages instead).
 */
export function dueInfo(item: InboxItem, now: Date = new Date()): DueInfo | null {
  if (!item.dueAt) {
    return null;
  }
  const due = new Date(item.dueAt);
  const remaining = due.getTime() - now.getTime();
  // Due dates are 23:59:59 UTC on the due day — rendering that in a
  // zone ahead of UTC (BST included) rolls it into the next morning and
  // reads a day late, so pin the display to UTC. The remaining-time
  // maths above is a true instant comparison and needs no such care.
  const exact = due.toLocaleString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  });
  if (remaining < 0) {
    return { label: `${coarse(-remaining)} over`, tone: 'overdue', exact };
  }
  const tatMs = item.turnaroundDays ? item.turnaroundDays * DAY : null;
  const warningWindow = Math.max(DAY, tatMs ? tatMs * 0.2 : 0);
  return {
    label: coarse(remaining),
    tone: remaining <= warningWindow ? 'warning' : 'quiet',
    exact,
  };
}

// ---------------------------------------------------------------------------
// Waiting-row ball-holder + staleness
// ---------------------------------------------------------------------------

const BALL_HOLDER: Record<string, string> = {
  report_pending_activation: 'client',
  np_fulfilment_pending: 'partner',
};

/** "Waiting on client · since 9 Jul" for waiting-court rows. */
export function waitingNote(item: InboxItem): string | null {
  if (item.court !== 'waiting') {
    return null;
  }
  const holder = BALL_HOLDER[item.kind] ?? 'someone else';
  const since = new Date(item.createdAt).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
  });
  return `Waiting on ${holder} · since ${since}`;
}

/** Grey "quiet 6d" note for waiting rows with no clock and no recent
 * activity — the queue-hygiene backstop, deliberately not red. */
export function staleNote(item: InboxItem, now: Date = new Date()): string | null {
  if (item.court !== 'waiting' || item.dueAt) {
    return null;
  }
  const last = new Date(item.updatedAt ?? item.createdAt).getTime();
  const days = Math.floor((now.getTime() - last) / DAY);
  return days >= 4 ? `quiet ${days}d` : null;
}

// ---------------------------------------------------------------------------
// Commercials + analyst display
// ---------------------------------------------------------------------------

/** "£1,500 · TAT 5d · Expedited" — compact, muted, line-2 material. */
export function commercialsLine(item: InboxItem): string | null {
  const parts: string[] = [];
  const money = formatMoney(item.price, item.currency);
  if (money) {
    parts.push(money);
  }
  if (item.turnaroundDays != null) {
    parts.push(`TAT ${item.turnaroundDays}d`);
  }
  if (item.expedited) {
    parts.push('Expedited');
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** Initials for the analyst avatar — from the resolved name when we
 * have one, else the first two characters of the user id. */
export function analystInitials(name: string | null, userId: string): string {
  if (name) {
    const words = name.trim().split(/\s+/);
    const first = words[0]?.[0] ?? '';
    const last = words.length > 1 ? (words[words.length - 1][0] ?? '') : '';
    return `${first}${last}`.toUpperCase() || userId.slice(0, 2).toUpperCase();
  }
  return userId.slice(0, 2).toUpperCase();
}
